import type { AssetType } from '~/types';
import { DEFAULT_THEME, type Theme } from '@workramp/ui';
import chroma from 'chroma-js';
import { ReportType } from '~/types';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import isFeatureEnabled from '../components/common/is-feature-enabled';
import { getCurrentUser } from '../common/explicit-exports';

export function getColorFromType(theme: Theme, type: ReportType | AssetType) {
  switch (type) {
    case ReportType.TRAININGS:
    case ReportType.GUIDES:
    case AssetPickerTypes.GUIDE:
      return theme.colors.decorative.blue[500];
    case ReportType.CHALLENGES:
    case AssetPickerTypes.CHALLENGE:
      return theme.colors.decorative.orange[500];
    case ReportType.CONTENT_PROVIDER_CONTENT:
    case ReportType.SCORM:
    case AssetPickerTypes.SCORM:
      return theme.colors.decorative.orange[500];
    case ReportType.SERIES:
    case AssetPickerTypes.SERIES:
    case AssetPickerTypes.PATH:
      return theme.colors.decorative.green[500];
    case ReportType.EVENTS:
    case AssetPickerTypes.EVENT:
    case AssetPickerTypes.CLASS:
      return theme.colors.decorative.red[500];
    case ReportType.RESOURCES:
    case AssetPickerTypes.RESOURCE:
      return theme.colors.decorative.teal[500];
    case ReportType.LIBRARIES:
    case AssetPickerTypes.LIBRARY:
    case AssetPickerTypes.UNIVERSITY:
      return theme.colors.decorative.purple[500];
    default:
      return theme.colors.on[600];
  }
}
export const getPalette = (color: string) => {
  const lightColor = chroma.distance('white', color);

  // Get the distance between white and the color
  // so we never brighten the color too much
  const lightness = (lightColor * 3) / 100;

  return {
    50: chroma(color).brighten(lightness).hex(),
    100: chroma(color)
      .brighten((lightness * 5) / 6)
      .hex(),
    200: chroma(color)
      .brighten((lightness * 2) / 3)
      .hex(),
    300: chroma(color)
      .brighten(lightness / 2)
      .hex(),
    400: chroma(color)
      .brighten(lightness / 3)
      .hex(),
    500: chroma(color)
      .brighten(lightness / 6)
      .hex(),
    600: color,
    700: chroma(color)
      .darken(lightness / 6)
      .hex(),
    800: chroma(color)
      .darken(lightness / 3)
      .hex(),
    900: chroma(color)
      .darken(lightness / 2)
      .hex(),
  };
};

export const getEnterpriseColor = () => {
  const currentUser = getCurrentUser();
  return isFeatureEnabled('fy25_q4_brand_color_infusion')
    ? currentUser?.enterprise.scheme_color
    : DEFAULT_THEME.colors.primary;
};

export const getEnterprisePalette = () => {
  const enterpriseColor = getEnterpriseColor();
  return enterpriseColor && isFeatureEnabled('fy25_q4_brand_color_infusion')
    ? getPalette(enterpriseColor as string)
    : DEFAULT_THEME.colors.primary;
};

export const getIconColor = (defaultColor: string) => {
  const currentUser = getCurrentUser();
  const enterpriseColor = getEnterpriseColor();
  return enterpriseColor &&
    currentUser?.enterprise.showSchemeColorInContentIconography &&
    isFeatureEnabled('fy25_q4_brand_color_infusion')
    ? (enterpriseColor as string)
    : defaultColor;
};

/**
 * WCAG 2.0 level AA requires a contrast ratio of at least 4.5:1 for normal text
 * and 3:1 for large text. WCAG 2.1 requires a contrast ratio of at least 3:1 for graphics
 * and user interface components (such as form input borders). WCAG Level AAA requires
 * a contrast ratio of at least 7:1 for normal text and 4.5:1 for large text.
 * https://webaim.org/resources/contrastchecker/
 */

type ComplianceLevel = 'AA' | 'AAA';
type ComplianceElement = 'LargeText' | 'Graphics' | 'NormalText';

export const isAccessibleColor = (
  color: string,
  complianceLevel: ComplianceLevel = 'AA',
  element: ComplianceElement = 'LargeText',
) => {
  const contrast = chroma.contrast('white', color);

  if (complianceLevel === 'AA') {
    if (element === 'LargeText' || element === 'Graphics') {
      return contrast > 3;
    }
    return contrast > 4.5;
  }

  if (element === 'NormalText') {
    return contrast > 7;
  }

  return contrast > 4.5;
};
